/**
 * Idle detection and time-on-module tracking for the course player.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useAsterController } from "context";
import { actionModuleInspectEdit } from "context/action";
import { PENDING_INSPECT_KEY } from "offline/constants";
import { mapSectionToGradeDetails } from "utils/grade.util";
import { getProblemBlockNumber, getSectionTitle, isVerticalQuiz } from "utils/course";
import { BModal } from "components";
import { VButton, VText } from "form";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
const SAVE_EVERY_MS = 60 * 1000;
const REFRESH_AFTER_MS = 60 * 1000;

const emptyTracker = () => ({
  course_id: null,
  hash: null,
  module_title: null,
  section_index: null,
  module_start: null,
  total_spent: 0,
  quiz_start: null,
  quiz_spent: 0,
  correct_count: 0,
  total_count: 0,
  stopped: false,
});

window.tracker = emptyTracker();

const isGhost = () => Boolean(localStorage.getItem("jwt-ghost"));

/**
 * Course player idle detector. Tracks time-on-module and prompts after inactivity.
 *
 * @param {{ idleMinutes?: number }} props
 */
const BoardIdle = ({ idleMinutes = 12 }) => {
  const { t } = useTranslation("common");
  const { courseId, sectionIndex, sequenceIndex, verticalIndex } = useParams();
  const [controller, dispatch] = useAsterController();
  const { user, structure, courses } = controller;

  const [prompt, setPrompt] = useState(null);
  const promptRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const setPromptState = (next) => {
    promptRef.current = next;
    setPrompt(next);
  };

  const accumulate = useCallback(() => {
    const tr = window.tracker;
    if (isGhost() || tr.stopped) return;
    const now = Date.now();
    if (tr.module_start) {
      tr.total_spent += (now - tr.module_start) / 1000;
      tr.module_start = now;
    }
    if (tr.quiz_start) {
      tr.quiz_spent += (now - tr.quiz_start) / 1000;
      tr.quiz_start = now;
    }
  }, []);

  const persist = useCallback((opts = {}) => {
    if (isGhost() || window.tracker.stopped) return Promise.resolve();
    if (!opts.skipAccumulate) accumulate();
    const tr = window.tracker;
    const total_spent = Math.round(tr.total_spent);
    const quiz_spent = Math.round(tr.quiz_spent);
    if (!tr.course_id || !tr.module_title || (total_spent <= 0 && quiz_spent <= 0)) return Promise.resolve();

    const payload = {
      course_id: tr.course_id,
      hash: tr.hash,
      module_title: tr.module_title,
      total_spent,
      quiz_spent,
      correct_count: tr.correct_count,
      total_count: tr.total_count,
      progress: window.courseProgress || 0,
    };
    const pending = actionModuleInspectEdit(dispatch, payload);
    tr.total_spent = 0;
    tr.quiz_spent = 0;
    return Promise.resolve(pending).then(() => {
      try {
        const raw = localStorage.getItem(PENDING_INSPECT_KEY);
        if (!raw) return;
        const snap = JSON.parse(raw);
        if (
          snap.course_id === payload.course_id &&
          snap.module_title === payload.module_title &&
          snap.total_spent === payload.total_spent &&
          snap.quiz_spent === payload.quiz_spent
        ) {
          localStorage.removeItem(PENDING_INSPECT_KEY);
        }
      } catch {
        /* private mode */
      }
    });
  }, [accumulate, dispatch]);

  const snapshotPendingInspect = useCallback(() => {
    if (isGhost() || window.tracker.stopped) return;
    accumulate();
    const tr = window.tracker;
    const total_spent = Math.round(tr.total_spent);
    const quiz_spent = Math.round(tr.quiz_spent);
    if (!tr.course_id || !tr.module_title || (total_spent <= 0 && quiz_spent <= 0)) return;
    try {
      localStorage.setItem(
        PENDING_INSPECT_KEY,
        JSON.stringify({
          course_id: tr.course_id,
          hash: tr.hash,
          module_title: tr.module_title,
          total_spent,
          quiz_spent,
          correct_count: tr.correct_count,
          total_count: tr.total_count,
          progress: window.courseProgress || 0,
        })
      );
    } catch {
      /* quota / private mode */
    }
  }, [accumulate]);

  const stopTracking = useCallback(() => {
    persist();
    window.tracker.stopped = true;
    window.tracker.module_start = null;
    window.tracker.quiz_start = null;
  }, [persist]);

  useEffect(() => {
    if (isGhost() || window.tracker.stopped) return;

    const course = (courses.data || []).find((c) => c.id === courseId) || {};
    const tr = window.tracker;
    const title = getSectionTitle(structure, sectionIndex);

    if (tr.section_index && sectionIndex && tr.section_index !== sectionIndex) persist();

    tr.course_id = courseId;
    tr.hash = user.hash;
    tr.module_title = title || tr.module_title;
    tr.total_count = getProblemBlockNumber(structure, sectionIndex, sequenceIndex);
    tr.section_index = sectionIndex;
    if (!tr.module_start) tr.module_start = Date.now();

    const mapper = mapSectionToGradeDetails(course)
      .filter((m) => m.details && m.cutoff !== 0)
      .find((m) => m.sectionTitle === tr.module_title);
    if (mapper && mapper.raw_earned !== undefined) tr.correct_count = mapper.raw_earned;

    const isQuiz = isVerticalQuiz(course.moduleMapper, structure, Number(sectionIndex), Number(sequenceIndex), Number(verticalIndex));
    if (isQuiz) {
      if (!tr.quiz_start) tr.quiz_start = Date.now();
    } else if (tr.quiz_start) {
      tr.quiz_spent += (Date.now() - tr.quiz_start) / 1000;
      tr.quiz_start = null;
    }
  }, [courseId, courses.data, persist, sectionIndex, sequenceIndex, structure, user.hash, verticalIndex]);

  useEffect(() => {
    if (isGhost()) return undefined;

    try {
      const raw = localStorage.getItem(PENDING_INSPECT_KEY);
      if (raw) {
        const snap = JSON.parse(raw);
        if (snap?.course_id && snap.module_title) {
          Promise.resolve(actionModuleInspectEdit(dispatch, snap)).then(() => {
            try {
              localStorage.removeItem(PENDING_INSPECT_KEY);
            } catch {
              /* private mode */
            }
          });
        } else {
          localStorage.removeItem(PENDING_INSPECT_KEY);
        }
      }
    } catch {
      try {
        localStorage.removeItem(PENDING_INSPECT_KEY);
      } catch {
        /* private mode */
      }
    }

    lastActivityRef.current = Date.now();
    const idleLimitMs = idleMinutes * 60 * 1000;

    const onActivity = () => {
      if (promptRef.current) return;
      lastActivityRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    // SCORM/LTI run in iframes — parent window events do not fire while focused there.
    const onFocusIn = (e) => {
      if (e?.target?.tagName === "IFRAME" || e?.target?.closest?.(".v-content-iframe")) onActivity();
    };
    document.addEventListener("focusin", onFocusIn, true);
    const iframePulse = setInterval(() => {
      const active = document.activeElement;
      if (active && active.tagName === "IFRAME") onActivity();
    }, 5000);

    const idleTick = setInterval(() => {
      if (isGhost() || window.tracker.stopped || promptRef.current) return;
      if (Date.now() - lastActivityRef.current < idleLimitMs) return;

      setPromptState("studying");
      stopTracking();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => {
        setPromptState("refresh");
      }, REFRESH_AFTER_MS);
    }, 1000);

    const saveTick = setInterval(persist, SAVE_EVERY_MS);
    const onHidden = () => {
      if (document.hidden) persist();
    };
    const onUnload = () => {
      snapshotPendingInspect();
      persist({ skipAccumulate: true });
    };

    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onUnload);
    window.addEventListener("beforeunload", onUnload);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      document.removeEventListener("focusin", onFocusIn, true);
      clearInterval(iframePulse);
      clearInterval(idleTick);
      clearInterval(saveTick);
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onUnload);
      window.removeEventListener("beforeunload", onUnload);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      persist();
      window.tracker = emptyTracker();
    };
  }, [dispatch, idleMinutes, persist, snapshotPendingInspect, stopTracking]);

  const onStillStudying = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    lastActivityRef.current = Date.now();
    setPromptState(null);
  };

  if (isGhost()) return null;

  return (
    <BModal open={Boolean(prompt)} width="90vw" height="fit-content" className="w-full max-w-xl max-h-150 p-8 flex flex-col items-center">
      <VText className="flex items-center color-primary font-bold text-lg" color="custom" div>
        <Icon icon="ci:warning-outline" className="flex-shrink-0" />
        &nbsp;{prompt === "refresh" ? t("You need to refresh the page.") : t("Are you studying now?")}
      </VText>
      {prompt === "refresh" ? (
        <VButton variant="contained" className="mt-4" onClick={() => document.location.reload()}>
          {t("OK")}
        </VButton>
      ) : (
        <VButton variant="contained" className="mt-4" onClick={onStillStudying}>
          {t("Yes")}
        </VButton>
      )}
    </BModal>
  );
};

BoardIdle.propTypes = {
  idleMinutes: PropTypes.number,
};
BoardIdle.defaultProps = {
  idleMinutes: 12,
};

export default BoardIdle;
